import uuid from './client-id';
import { PageView } from './events/page-view';
import { BrowsingStart } from './events/browsing-start';
import { NotSupportedError } from './errors/index';
import { DefaultBrowserAccessor, IBrowserAccessor, IBrowserStorage, ShopifyBrowserAccessor } from './browser';
import { IEventSender } from './event-senders/event-sender';
import { UtmHandler, UtmPreset } from './utm';
import { Http, IHttp } from './http';
import { PostRequestEventSender } from './event-senders/post-request';
import { TrackingPixelEventSender } from './event-senders/tracking-pixel';
import { ConsoleLogger, DataTrackingServiceLogger, HiddenLogger, ILogger } from './logger';
import { expect } from './expect';
import { CheckoutStart } from './events/checkout-start';
import { CheckoutFunnelEvent } from './events/checkout-funnel';
import { BrowsingFunnelEvent } from './events/browsing-funnel';
import { GetRequestEventSender } from './event-senders/get-request';
import { MerchantServerConfiguration } from './interfaces/merchant-server-configuration.interface';
import { DataStorageManager } from './data-storage-manager';
import { GoogleAdsHandler } from './google-ads';
import { GoogleAdsPreset } from './google-ads.types';
import { BorderfreeHandler, BorderfreePreset } from './borderfree';
import { NoOpEventSender } from './event-senders/no-oop';

enum EventCategory {
  BrowsingFunnel = 'Browsing Funnel',
  CheckoutFunnel = 'Checkout Funnel',
}

export enum EventSendingStrategy {
  TrackingPixel,
  GetRequest,
  PostRequest,
  CustomImplementation,
}

enum LoggingStrategy {
  Hidden, // writes logs to the variable making it not visible in console
  Console,
  DataTrackingService,
}

type AnalyticsSDKSnapshot = {
  merchantId: string;
  shopperCountryCode: string;
  cdn: string;
  clientId: string;
  sessionId: string;
  sessionIdExpiry: number;

  configurations: {
    eventSendingStrategy: EventSendingStrategy;
  };

  featureToggles: {
    FT_3DA: boolean;
    FT_3DA_UTM_SOURCE_LIST: string[];
    FT_3DA_STORAGE_LIFETIME: number;
    FT_BF_GOOGLE_ADS: boolean;
    FT_BF_GOOGLE_ADS_LIFETIME: number;
    isOperatedByGlobalE: boolean;
  };

  lockBrowsingStartOnSessionId: string;
  lockCheckoutStartOnSessionId: string;

  dataUpdatedAt: number;
  dataLayer: object;
};

type SendEventQueueItem = {
  functionName: string;
  arguments: IArguments;
};

export type AnalyticsClientConfiguration = {
  cdn: string;
  eventSendingStrategy?: EventSendingStrategy;
  eventSender?: IEventSender;
  browser: ShopifyOverrides | Window;
  loggingStrategy?: LoggingStrategy;
  logger?: ILogger;
  clientId?: string; // if Client ID cannot be accessed from storage it could be provided manually
  sessionId?: string;
  utms?: UtmPreset;
  googleAds?: GoogleAdsPreset;
  borderfree?: BorderfreePreset;
};

export type ShopifyOverrides = {
  localStorage: IBrowserStorage;
  sessionStorage: IBrowserStorage;
  cookieStorage: IBrowserStorage;
};

/**
 * Represents API to collect and send analytics data.
 *
 * The Analytics SDK is a lightweight JavaScript library designed to collect analytics data on merchant’s website.
 * It provides simple API to generate Browsing Funnel and Checkout Funnel events and collects essential data like
 * browser information, UTM parameters, and session details and sends them to a server for analytics purposes.
 */
export class AnalyticsClient {
  // constants
  private readonly ANALYTICS_STORAGE_KEY = 'GlobalE_Analytics';
  private readonly DATA_TRACKING_SERVICE_API_URL: string;
  private readonly MONOLITH_API_URL: string;

  // dependencies
  private readonly http: IHttp;
  private logger: ILogger;
  private loggingStrategy: LoggingStrategy;
  private browser: IBrowserAccessor;
  private dataStorageManager: DataStorageManager;

  // sdk configuration variables
  private merchantId: string;
  private shopperCountryCode: string;
  private cdn: string;
  private eventSendingStrategy: EventSendingStrategy; // web pixel / post request / some other way
  private eventSender: IEventSender;
  private eventQueue: Array<SendEventQueueItem> = [];
  // sdk internal flags
  private isConfigurationLoaded: boolean = false;
  private isInitialized: boolean = false;
  private lockBrowsingStartOnSessionId: string;
  private lockCheckoutStartOnSessionId: string;
  // sdk feature toggles
  private FT_3DA: boolean = true;
  private FT_3DA_UTM_SOURCE_LIST: string[] = ['borderfree'];
  private FT_3DA_STORAGE_LIFETIME: number = 4320; // minutes
  private FT_BF_GOOGLE_ADS: boolean = false;
  private FT_BF_GOOGLE_ADS_LIFETIME: number = 30; // days
  private isOperatedByGlobalE: boolean = true; // default true just in case if server doesn't return this value

  // the client data
  private clientId: string;
  private sessionId: string;
  private sessionIdExpiry: number;
  private userAgent: string;
  private screenResolution: string;
  private dataLayer: object;

  // the UTM parameters
  private utms: UtmHandler;
  private googleAds: GoogleAdsHandler;
  private borderfreeHandler: BorderfreeHandler;
  dataUpdatedAt: number;

  public get ClientId() {
    return this.clientId;
  }

  public get SessionId() {
    return this.sessionId;
  }

  public get UtmSource() {
    return this.utms.utmSource;
  }

  public get UtmMedium() {
    return this.utms.utmMedium;
  }

  public get UtmCampaign() {
    return this.utms.utmCampaign;
  }

  public get googleClickId() {
    return this.googleAds?.googleClickId;
  }

  /** Gets true if Borderfree is enabled and */
  public get isBorderfreeGoogleClickId() {
    return this.googleAds?.isBorderfreeGoogleClickId;
  }

  private readonly ExpirationTime = 30;
  /**
   * Initializes a new instance of Analytics SDK object with given dependencies (e.g. HttpService, UUID utility etc).
   */
  public constructor(http?: IHttp, logger?: ILogger) {
    // the process.env.CONSTANT will be replaced by esbuild
    this.DATA_TRACKING_SERVICE_API_URL = process.env.DATA_TRACKING_SERVICE_API_URL;
    this.MONOLITH_API_URL = process.env.MONOLITH_API_URL;

    this.http = http ?? new Http();
    this.logger = logger ?? new HiddenLogger();
  }

  /** Loads SDK's configurations and cached data from Local Storage. */
  private async loadConfigurationSnapshot(): Promise<void> {
    try {
      const snapshot = await this.dataStorageManager.loadData<AnalyticsSDKSnapshot>(this.ANALYTICS_STORAGE_KEY);

      if (snapshot === null) {
        this.logger.writeInfoLog({
          method: 'AnalyticsSDK::loadConfigurationSnapshot()',
          message: 'Snapshot is not found in Local Storage',
        });
        return;
      }

      // required fields
      this.merchantId = expect(snapshot.merchantId).toBeDefined('Snapshot integrity violated. The `merchantId` is missing.');
      this.shopperCountryCode = snapshot.shopperCountryCode;
      this.cdn = expect(snapshot.cdn).toBeDefined('Snapshot integrity violated. The `cdn` is missing.');
      this.clientId = expect(snapshot.clientId).toBeDefined('Snapshot integrity violated. The `clientId` is missing.');
      this.sessionId = snapshot.sessionId;
      this.sessionIdExpiry = snapshot.sessionIdExpiry;
      // configurational fields
      this.eventSendingStrategy = snapshot.configurations.eventSendingStrategy;
      // feature toggles
      this.FT_3DA = snapshot.featureToggles.FT_3DA;
      this.FT_3DA_UTM_SOURCE_LIST = snapshot.featureToggles.FT_3DA_UTM_SOURCE_LIST;
      this.FT_3DA_STORAGE_LIFETIME = snapshot.featureToggles.FT_3DA_STORAGE_LIFETIME;
      this.FT_BF_GOOGLE_ADS = snapshot.featureToggles.FT_BF_GOOGLE_ADS;
      this.FT_BF_GOOGLE_ADS_LIFETIME = snapshot.featureToggles.FT_BF_GOOGLE_ADS_LIFETIME;
      this.isOperatedByGlobalE = snapshot.featureToggles.isOperatedByGlobalE;
      // optional fields

      this.lockBrowsingStartOnSessionId = snapshot.lockBrowsingStartOnSessionId;
      this.lockCheckoutStartOnSessionId = snapshot.lockCheckoutStartOnSessionId;

      this.dataUpdatedAt = snapshot.dataUpdatedAt;
      this.dataLayer = snapshot.dataLayer;

      this.isConfigurationLoaded = true;
    } catch (error) {
      this.logger.writeWarningLog({
        method: 'AnalyticsSDK::loadConfigurationSnapshot()',
        message: error.message,
        errorMessage: error.message,
      });
    }
  }

  /** Saves SDK's configurations and cached data to Local Storage and Cookies. */
  private async saveConfigurationSnapshot() {
    const snapshot = {
      // required fields
      merchantId: this.merchantId,
      shopperCountryCode: this.shopperCountryCode,
      cdn: this.cdn,
      clientId: this.clientId,
      sessionId: this.sessionId,
      sessionIdExpiry: this.sessionIdExpiry,
      // configurational fields
      configurations: {
        eventSendingStrategy: this.eventSendingStrategy,
      },
      // feature toggles
      featureToggles: {
        FT_3DA: this.FT_3DA,
        FT_3DA_UTM_SOURCE_LIST: this.FT_3DA_UTM_SOURCE_LIST,
        FT_3DA_STORAGE_LIFETIME: this.FT_3DA_STORAGE_LIFETIME,
        FT_BF_GOOGLE_ADS: this.FT_BF_GOOGLE_ADS,
        FT_BF_GOOGLE_ADS_LIFETIME: this.FT_BF_GOOGLE_ADS_LIFETIME,
        isOperatedByGlobalE: this.isOperatedByGlobalE,
      },
      // optional fields
      lockBrowsingStartOnSessionId: this.lockBrowsingStartOnSessionId,
      lockCheckoutStartOnSessionId: this.lockCheckoutStartOnSessionId,

      dataUpdatedAt: Date.now(),
      dataLayer: this.dataLayer,
    } as AnalyticsSDKSnapshot;

    await this.dataStorageManager.saveData<AnalyticsSDKSnapshot>(this.ANALYTICS_STORAGE_KEY, snapshot);
  }

  /** Loads SDK's configurations and cached data from Monolith. */
  private async loadConfigurationFromServer(): Promise<void> {
    try {
      const endpoint = new URL(`merchant/analytics/sdk/init/${this.merchantId}/${this.shopperCountryCode}`, this.cdn);
      endpoint.searchParams.append('merchantId', this.merchantId);

      const response = await this.http.get<MerchantServerConfiguration>(endpoint.toString());

      this.FT_3DA = response.FT_UtmReplaceSessionStorageWithCookies;
      this.FT_3DA_UTM_SOURCE_LIST = response.UtmAcceptableUtmSourceList;
      this.FT_3DA_STORAGE_LIFETIME = response.UtmParamsCookieLifetimeInMinutes;
      this.FT_BF_GOOGLE_ADS = response.BfGoogleAdsEnabled;
      this.FT_BF_GOOGLE_ADS_LIFETIME = response.BfGoogleAdsLifetimeInDays;
      if (Object.keys(response).includes('IsOperatedByGlobalE')) {
        this.isOperatedByGlobalE = response.IsOperatedByGlobalE;
      }
    } catch (error) {
      this.logger.writeErrorLog({
        method: 'AnalyticsSDK::loadMerchantConfiguration()',
        message: error.message,
        errorMessage: error.message,
      });
    }
  }

  /** Generates a unique identifier of the client. */
  private setClientId(clientIdFromConfiguration?: string): void {
    if (clientIdFromConfiguration) {
      this.clientId = clientIdFromConfiguration;
    } else if (!this.clientId) {
      this.clientId = uuid();
    }
  }

  private setSessionId(sessionId: string): void {
    const timestamp = Date.now();

    if (!sessionId) {
      // Case: no new session ID provided — do nothing
      return;
    }

    // If current sessionId is missing OR differs from incoming — accept new one
    if (!this.sessionId || this.sessionId !== sessionId) {
      this.sessionId = sessionId;
      this.sessionIdExpiry = timestamp + this.ExpirationTime * 60 * 1000;
      return;
    }

    // 1. sessionId is same and not expired — do nothing -> will be handled on updateSession func
    // 2. sessionId is same and has expired — do nothing -> will be handled on updateSession func (TODO: server sides events needs to be aligned with the updateSession functionality as well)
  }

  private setShopperCountryCode(shopperCountryCode: string) {
    this.shopperCountryCode = shopperCountryCode;
  }

  private defineBrowserAccessor(configuration: AnalyticsClientConfiguration): void {
    if (configuration.browser === undefined || configuration.browser === null) {
      throw new Error('The configuration.browser property should be defined.');
    } else if (isWindow(configuration.browser)) {
      this.browser = new DefaultBrowserAccessor(configuration.browser);
    } else if (isBrowserOverrides(configuration.browser)) {
      this.browser = new ShopifyBrowserAccessor(configuration.browser.sessionStorage, configuration.browser.localStorage, configuration.browser.cookieStorage);
    } else {
      throw new Error('The provided configuration.browser is not supported (could be missing required properties of any of supported types).');
    }

    function isWindow(value: unknown): value is Window {
      return typeof value === 'object' && value !== null && 'document' in value;
    }

    function isBrowserOverrides(value: unknown): value is ShopifyOverrides {
      return typeof value === 'object' && value !== null && 'localStorage' in value && 'sessionStorage' in value && 'cookieStorage' in value;
    }
  }

  private defineEventSender(configuration: AnalyticsClientConfiguration): void {
    if (!this.isOperatedByGlobalE) {
      this.eventSender = new NoOpEventSender();
      return;
    }
    this.eventSendingStrategy = configuration.eventSendingStrategy || EventSendingStrategy.TrackingPixel;

    switch (this.eventSendingStrategy) {
      case EventSendingStrategy.TrackingPixel:
        this.eventSender = new TrackingPixelEventSender(this.DATA_TRACKING_SERVICE_API_URL, this.browser);
        break;
      case EventSendingStrategy.GetRequest:
        this.eventSender = new GetRequestEventSender(this.DATA_TRACKING_SERVICE_API_URL, this.http);
        break;
      case EventSendingStrategy.PostRequest:
        this.eventSender = new PostRequestEventSender(this.DATA_TRACKING_SERVICE_API_URL, this.http);
        break;
      case EventSendingStrategy.CustomImplementation:
        this.eventSender = configuration.eventSender;
        break;

      default:
        throw new NotSupportedError(`enum value ${this.eventSendingStrategy}`);
    }
  }

  private defineLogger(configuration: AnalyticsClientConfiguration): void {
    this.loggingStrategy = configuration.loggingStrategy ?? LoggingStrategy.Hidden;

    switch (this.loggingStrategy) {
      case LoggingStrategy.Hidden:
        this.logger = new HiddenLogger();
        break;
      case LoggingStrategy.Console:
        this.logger = new ConsoleLogger();
        break;
      case LoggingStrategy.DataTrackingService:
        this.logger = new DataTrackingServiceLogger(new URL('logger', this.DATA_TRACKING_SERVICE_API_URL).toString(), { http: this.http });
        break;

      default:
        throw new NotSupportedError('Custom logger currently not supported.');
    }
  }

  private defineUtmParametersHandler() {
    this.utms = new UtmHandler(this.browser, this.logger, {
      FT_3DA: this.FT_3DA,
      FT_3DA_UTM_SOURCE_LIST: this.FT_3DA_UTM_SOURCE_LIST,
      FT_3DA_STORAGE_LIFETIME: this.FT_3DA_STORAGE_LIFETIME,
    });
  }

  private defineGoogleAdsHandler() {
    this.googleAds = new GoogleAdsHandler(this.browser, this.logger, {
      bfGoogleAdsEnabled: this.FT_BF_GOOGLE_ADS,
      bfGoogleAdsLifetimeInDays: this.FT_BF_GOOGLE_ADS_LIFETIME,
    });
  }

  private defineBorderfreeParametersHandler() {
    this.borderfreeHandler = new BorderfreeHandler(this.browser, this.logger, {
      FT_3DA: this.FT_3DA,
      FT_3DA_UTM_SOURCE_LIST: this.FT_3DA_UTM_SOURCE_LIST,
      FT_3DA_STORAGE_LIFETIME: this.FT_3DA_STORAGE_LIFETIME,
    });
  }

  private defineDataStorageManager() {
    this.dataStorageManager = new DataStorageManager(this.browser, this.logger);
  }

  private async updateSession(timestamp: number): Promise<void> {
    // if current Session ID expired, then generate a new one
    if (this.sessionId === undefined || this.sessionIdExpiry < timestamp) {
      this.sessionId = uuid();
    }
    // the expiration time set in 30 mins of current event timestamp
    this.sessionIdExpiry = timestamp + this.ExpirationTime * 60 * 1000;
    await this.saveConfigurationSnapshot();
  }

  private queueEventSending(functionName: string, args: IArguments) {
    this.eventQueue.push({
      functionName: functionName,
      arguments: args,
    });
  }

  private queueEventDequeue() {
    while (this.eventQueue.length > 0) {
      const item = this.eventQueue.pop();
      if (item && item.arguments) {
        const argsArray = Array.prototype.slice.call(item.arguments); // Convert using slice
        this[item.functionName](...argsArray); // Spread the array
      } else {
        this.logger.writeInfoLog('Arguments are undefined or missing for this event');
      }
    }
  }

  /**
   * Initializes SDK for given merchant with given configuration.
   */
  public async init(merchantId: string, shopperCountryCode: string, configuration: AnalyticsClientConfiguration) {
    // ensure required parameters assigned to load configuration
    this.merchantId = expect(merchantId).toBeDefined('The merchantId is required to load configuration.');
    this.shopperCountryCode = expect(shopperCountryCode).toBeDefined('The shopper country is required to load configuration.');
    this.cdn = expect(configuration?.cdn).toBeDefined('The CDN currently is required to load configuration. (should be changed in 2025 Q1)');

    // initialize browser accessor before anything else to get access to Session & Local Storage
    this.defineBrowserAccessor(configuration);
    this.defineLogger(configuration);
    this.defineDataStorageManager();

    // get cached configuration or load it from server (1. no valid cached data, 2. if provided country changed from cached one)
    await this.loadConfigurationSnapshot();
    if (!this.isConfigurationLoaded || this.shopperCountryCode !== shopperCountryCode) {
      this.setShopperCountryCode(shopperCountryCode);
      await this.loadConfigurationFromServer();
    }
    // Define event sender after loading configuration to apply possible changes from server (e.g. operated country)
    this.defineEventSender(configuration);

    // construct marketing traffic handlers
    this.defineUtmParametersHandler();
    this.defineGoogleAdsHandler();
    this.defineBorderfreeParametersHandler();

    // generate and set Client ID if not yet generated
    this.setClientId(configuration.clientId);
    this.setSessionId(configuration.sessionId);
    this.userAgent = this.browser.getUserAgent();
    this.screenResolution = this.browser.getScreenResolution();

    await this.utms.handle(configuration.utms);
    await this.googleAds.handle(configuration.googleAds);
    await this.borderfreeHandler.handle(configuration.borderfree ?? (configuration.utms as string), this.utms.utmSource);
    await this.saveConfigurationSnapshot();
    this.isInitialized = true;
    this.queueEventDequeue();
  }

  public async setMerchantCountryCode(shopperCountryCode: string): Promise<void> {
    this.shopperCountryCode = expect(shopperCountryCode).toBeDefined('The shopperCountryCode is required to setup Analytics SDK.');
    await this.saveConfigurationSnapshot();
  }
  public async setDataLayer(data: object): Promise<void> {
    this.dataLayer = data;
    await this.saveConfigurationSnapshot();
  }

  /** Sends "Browsing Start" event to Data Tracking Service. */
  public async sendBrowsingStartEvent(): Promise<boolean | undefined> {
    try {
      if (!this.isInitialized) {
        // eslint-disable-next-line prefer-rest-params
        this.queueEventSending(this.sendBrowsingStartEvent.name, arguments);
        return;
      }

      const timestamp = Date.now();
      await this.updateSession(timestamp);

      // make sure Browsing Start event sent once per session
      if (this.sessionId === this.lockBrowsingStartOnSessionId) {
        return false;
      } else {
        this.lockBrowsingStartOnSessionId = this.sessionId;
        await this.saveConfigurationSnapshot();
      }

      const event = new BrowsingStart(
        timestamp,
        this.clientId,
        this.sessionId,
        this.merchantId,
        this.shopperCountryCode,
        EventCategory.BrowsingFunnel,
        this.cdn,
        this.browser.getPageUrl(),
        this.browser.getPageTitle(),
        this.screenResolution,
        this.userAgent,
        this.browser.getReferrer(),
        this.utms.utmSource,
        this.utms.utmMedium,
        this.utms.utmCampaign,
        this.utms.utmTerm,
        this.utms.utmContent,
        this.utms.utmAdSet,
        this.borderfreeHandler.directToCheckoutEnabled || this.borderfreeHandler.directToCheckout
          ? {
              directToCheckout: this.borderfreeHandler.directToCheckout,
              directToCheckoutEnabled: this.borderfreeHandler.directToCheckoutEnabled,
            }
          : null,
      );

      await this.eventSender.send(event);
      return true;
    } catch (error) {
      this.logger.writeErrorLog({
        method: 'AnalyticsSDK::sendBrowsingStartEvent()',
        message: error.message,
        errorMessage: error.message,
      });
    }
  }

  /** Sends "Page View" event to Data Tracking Service. */
  public async sendPageViewEvent() {
    try {
      if (!this.isInitialized) {
        // eslint-disable-next-line prefer-rest-params
        this.queueEventSending(this.sendPageViewEvent.name, arguments);
        return;
      }

      const timestamp = Date.now();
      await this.updateSession(timestamp);

      const event = new PageView(
        timestamp,
        this.clientId,
        this.sessionId,
        this.merchantId,
        this.shopperCountryCode,
        EventCategory.BrowsingFunnel,
        this.cdn,
        this.browser.getPageUrl(),
        this.browser.getPageTitle(),
        this.screenResolution,
        this.userAgent,
        this.browser.getReferrer(),
        this.utms.utmSource,
        this.utms.utmMedium,
        this.utms.utmCampaign,
        this.utms.utmTerm,
        this.utms.utmContent,
        this.utms.utmAdSet,
        this.borderfreeHandler.directToCheckoutEnabled || this.borderfreeHandler.directToCheckout
          ? {
              directToCheckout: this.borderfreeHandler.directToCheckout,
              directToCheckoutEnabled: this.borderfreeHandler.directToCheckoutEnabled,
            }
          : null,
      );

      await this.eventSender.send(event);
    } catch (error) {
      this.logger.writeErrorLog({
        method: 'AnalyticsSDK::sendPageViewEvent()',
        message: error.message,
        errorMessage: error.message,
      });
    }
  }

  /** Sends custom Browsing Funnel event to Data Tracking Service. */
  public async sendBrowsingFunnelEvent(name: string, customData: object) {
    try {
      if (!this.isInitialized) {
        // eslint-disable-next-line prefer-rest-params
        this.queueEventSending(this.sendBrowsingFunnelEvent.name, arguments);
        return;
      }

      const timestamp = Date.now();
      await this.updateSession(timestamp);

      const event = new BrowsingFunnelEvent(
        name,
        timestamp,
        this.clientId,
        this.sessionId,
        this.merchantId,
        this.shopperCountryCode,
        EventCategory.BrowsingFunnel,
        this.cdn,
        this.browser.getPageUrl(),
        this.browser.getPageTitle(),
        this.screenResolution,
        this.userAgent,
        this.browser.getReferrer(),
        this.utms.utmSource,
        this.utms.utmMedium,
        this.utms.utmCampaign,
        this.utms.utmTerm,
        this.utms.utmContent,
        this.utms.utmAdSet,
        customData,
        this.borderfreeHandler.directToCheckoutEnabled || this.borderfreeHandler.directToCheckout
          ? {
              directToCheckout: this.borderfreeHandler.directToCheckout,
              directToCheckoutEnabled: this.borderfreeHandler.directToCheckoutEnabled,
            }
          : null,
      );

      await this.eventSender.send(event);
    } catch (error) {
      this.logger.writeErrorLog({
        method: 'AnalyticsSDK::sendBrowsingFunnelEvent()',
        message: error.message,
        errorMessage: error.message,
      });
    }
  }

  /** Sends "Checkout Start" event to Data Tracking Service. */
  public async sendCheckoutStartEvent(checkoutToken: string) {
    try {
      if (!this.isInitialized) {
        // eslint-disable-next-line prefer-rest-params
        this.queueEventSending(this.sendCheckoutStartEvent.name, arguments);
        return;
      }

      const timestamp = Date.now();
      await this.updateSession(timestamp);

      // make sure Checkout Start event sent once per session
      if (this.sessionId === this.lockCheckoutStartOnSessionId) {
        return false;
      } else {
        this.lockCheckoutStartOnSessionId = this.sessionId;
        await this.saveConfigurationSnapshot();
      }

      const event = new CheckoutStart(
        timestamp,
        this.clientId,
        this.sessionId,
        this.merchantId,
        this.shopperCountryCode,
        EventCategory.CheckoutFunnel,
        this.cdn,
        checkoutToken,
        this.userAgent,
        this.utms.utmSource,
        this.utms.utmMedium,
        this.utms.utmCampaign,
        this.utms.utmTerm,
        this.utms.utmContent,
        this.utms.utmAdSet,
        this.borderfreeHandler.directToCheckoutEnabled || this.borderfreeHandler.directToCheckout
          ? {
              directToCheckout: this.borderfreeHandler.directToCheckout,
              directToCheckoutEnabled: this.borderfreeHandler.directToCheckoutEnabled,
            }
          : null,
      );

      await this.eventSender.send(event);
    } catch (error) {
      this.logger.writeErrorLog({
        method: 'AnalyticsSDK::sendCheckoutStartEvent()',
        message: error.message,
        errorMessage: error.message,
      });
    }
  }

  /** Sends custom Checkout Funnel event to Data Tracking Service. */
  public async sendCheckoutFunnelEvent(checkoutToken: string, eventName: string, customData: object) {
    try {
      if (!this.isInitialized) {
        // eslint-disable-next-line prefer-rest-params
        this.queueEventSending(this.sendCheckoutFunnelEvent.name, arguments);
        this.logger.writeDebugLog('Exit sendCheckoutFunnelEvent without sending CheckoutFunnelEvent');
        return;
      }

      const timestamp = Date.now();
      await this.updateSession(timestamp);

      const event = new CheckoutFunnelEvent(
        eventName,
        timestamp,
        this.clientId,
        this.sessionId,
        this.merchantId,
        this.shopperCountryCode,
        EventCategory.CheckoutFunnel,
        this.cdn,
        checkoutToken,
        this.userAgent,
        this.utms.utmSource,
        this.utms.utmMedium,
        this.utms.utmCampaign,
        this.utms.utmTerm,
        this.utms.utmContent,
        this.utms.utmAdSet,
        { ...this.dataLayer, ...customData },
        this.borderfreeHandler.directToCheckoutEnabled || this.borderfreeHandler.directToCheckout
          ? {
              directToCheckout: this.borderfreeHandler.directToCheckout,
              directToCheckoutEnabled: this.borderfreeHandler.directToCheckoutEnabled,
            }
          : null,
      );

      this.logger.writeDebugLog('Sending CheckoutFunnelEvent');

      await this.eventSender.send(event);
    } catch (error) {
      this.logger.writeErrorLog({
        method: 'AnalyticsSDK::sendCheckoutFunnelEvent()',
        message: error.message,
        errorMessage: error.message,
      });
    }
  }
}

// todo: unit test: if window object is not accessable from root and not provided to analytics sdk ctor then custom implementation is required
